import { useCallback, useEffect, useMemo } from "react";
import type { ZodType } from "zod";
import { navigate } from "@/utilities/navigate";
import { useLocation } from "@/hooks/useLocation";
import { stringify, parse } from "query-string";
import localObject from "@/utilities/localObject";
import isEqual from "lodash/isEqual";

// key order in the url is incidental, so params are compared on a sorted form
// to avoid navigating when only the ordering differs
const canonicalize = (params: Record<string, unknown>) =>
  stringify(params, {
    arrayFormat: "index",
    sort: (a, b) => a.localeCompare(b),
  });

/*
 * options.forceUpdate triggers a navigation to the current path on mount.
 * Set it to true to fix an issue with a page scrolling to the top.
 */
const useSearchParams = <TModel>(
  schema: ZodType<TModel>,
  options?: {
    overrideBasePath?: string;
    forceUpdate?: boolean;
    persist?: string;
    persistExcludeKeys?: (keyof TModel)[];
  },
) => {
  const { search, pathnameWithoutLocale } = useLocation();
  const {
    overrideBasePath = "",
    forceUpdate = false,
    persist,
    persistExcludeKeys = [],
  } = options ?? {};

  const schemaDefaults = useMemo(() => {
    const result = schema.safeParse({});
    return result.success ? result.data : undefined;
  }, [schema]);

  // keys the schema does not declare belong to another consumer of the same url
  // (e.g. the deposit modal), so they have to survive this hook rebuilding the
  // query string rather than being dropped by the parse
  const schemaKeys = useMemo(() => {
    const shape = (schema as { shape?: Record<string, unknown> }).shape;

    return shape ? new Set(Object.keys(shape)) : undefined;
  }, [schema]);

  const parsedParams = useMemo(
    () => parse(search, { arrayFormat: "index" }),
    [search],
  );

  const storedDefaults = useMemo(() => {
    if (!persist) return undefined;

    const [stored] = localObject.getValue<Partial<TModel>>(persist, undefined);

    return stored ?? undefined;
  }, [persist, parsedParams]);

  const basePath = overrideBasePath ? overrideBasePath : pathnameWithoutLocale;

  const effectiveParams = useMemo(
    () => ({
      ...(storedDefaults ?? {}),
      ...parsedParams,
    }),
    [storedDefaults, parsedParams],
  );

  const result = schema.safeParse(effectiveParams);
  const safeParams = result.success ? result.data : schema.parse({});

  const normalizeValue = (value: unknown) =>
    Array.isArray(value)
      ? [...value].sort((a, b) => String(a).localeCompare(String(b)))
      : value;

  const isSameParamValue = (a: unknown, b: unknown) =>
    isEqual(normalizeValue(a), normalizeValue(b));

  const getParamsToStringify = <T extends Record<string, unknown>>(
    nextParams: T,
    schemaDefaults?: Partial<T> | null,
  ) => {
    if (schemaDefaults == null) {
      return nextParams;
    }

    const filtered: Partial<T> = {};

    Object.keys(nextParams).forEach((key) => {
      const typedKey = key as keyof T;
      const value = nextParams[typedKey];
      // a default can differ by other params, e.g. the default orderByDirection
      // differs by orderBy, so don't compare against the empty-url defaults
      const resolved = schema.safeParse({ ...nextParams, [key]: undefined });
      const defaultValue = resolved.success
        ? (resolved.data as Partial<T>)[typedKey]
        : schemaDefaults[typedKey];

      if (!isSameParamValue(value, defaultValue)) {
        filtered[typedKey] = value;
      }
    });

    return filtered;
  };

  const setSearchParams = useCallback(
    (params: Partial<TModel>, options?: { replace?: boolean }) => {
      const nextParams = {
        ...safeParams,
        ...params,
      };

      // Persist validated params if requested
      if (persist) {
        const persistableParams = { ...nextParams };

        persistExcludeKeys.forEach((key) => {
          delete persistableParams[key];
        });

        localObject.setValue(persist, persistableParams);
      }

      const foreignParams = schemaKeys
        ? Object.fromEntries(
            Object.entries(parsedParams).filter(
              ([key]) => !schemaKeys.has(key),
            ),
          )
        : {};

      const paramsToStringify = {
        ...foreignParams,
        ...getParamsToStringify(nextParams, schemaDefaults),
      };

      const mergedParams = stringify(paramsToStringify, {
        arrayFormat: "index",
      });

      if (canonicalize(paramsToStringify) === canonicalize(parsedParams)) {
        return;
      }

      navigate(
        mergedParams ? `${basePath}?${mergedParams}` : basePath,
        false,
        options,
      );
    },
    [pathnameWithoutLocale, basePath, search, persist, schema],
  );

  const removeSearchParam = useCallback(
    (keyToRemove: keyof TModel, options?: { replace?: boolean }) => {
      const nextParams = Object.fromEntries(
        Object.entries(parsedParams).filter(([key]) => key != keyToRemove),
      );

      // Persist validated params if requested
      if (persist) {
        const persistableParams = { ...nextParams };

        persistExcludeKeys.forEach((key) => {
          delete persistableParams[key as keyof typeof persistableParams];
        });

        localObject.setValue(persist, persistableParams);
      }

      const filteredParams = stringify(nextParams, {
        arrayFormat: "index",
      });

      navigate(
        filteredParams ? `${basePath}?${filteredParams}` : basePath,
        false,
        options,
      );
    },
    [parsedParams, basePath, search, persist, schema],
  );

  /**
   * Navigate to the current path to resolve an issue
   * where the page would scroll to the top when the page=2 appends.
   *
   * Running this causes the key to change from 'initial' to a number.
   * If this hook is used on a page that should NOT affect the path on first load,
   * setting 'forceUpdate' to 'true' will cause the browser's back button to unexpectedly appear.
   */
  useEffect(() => {
    if (forceUpdate) {
      setSearchParams(safeParams);
    }
  }, [forceUpdate, setSearchParams]);

  return {
    setSearchParams,
    searchParams: safeParams,
    removeSearchParam,
  } as const;
};

export { useSearchParams };
